import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../reducers';
import { delay, startWith, take } from 'rxjs/operators';
import { Observable } from 'rxjs/internal/Observable';
import { Subscription } from 'rxjs';
import * as fromJobs from '../../reducers';
import * as jobsActions from '../../actions/jobs.actions';
import { PublishJob } from '../../actions/jobs.actions';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SelectedJobState } from '../../reducers/jobs.reducer';
import * as moment from 'moment';
import 'moment/locale/nb';
import { CheckoutFormComponent } from '../../../job-sourcing-module/components/checkout-form/checkout-form.component';
import { PurchaseJobSourcingBoard } from '../../../job-sourcing-module/actions/job-sourcing.actions';
import { MatDialog } from '@angular/material';
import {
  JobBoard,
  JobSourcing,
} from '../../../job-sourcing-module/models/job-sourcing';
import { ChangeDetectionStrategy } from '@angular/core';
import * as Quill from 'quill';
import { LISTING_ITEM_TYPE } from '../../models/listing.model';

const pagesArr = ['info', 'job-listing', 'process', 'sourcing'];

@Component({
  selector: 'mj-edit-job-tpl-nav',
  templateUrl: './edit-job-tpl-nav.component.html',
  styleUrls: ['./edit-job-tpl-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditJobTplNavComponent implements OnInit, OnDestroy, OnChanges {
  currentRoute;
  pendingRequest$: Observable<boolean>;
  isJobValid$;
  isJobValidSub$;
  isValid: boolean = false;
  fillRequiredMsg: string = '';
  delete: string = 'Delete';
  deleteMsg: string = 'Are you sure you want to delete this job?';
  missingFieldsMsg: string = 'Fill all required fields first';
  @Input() jobState: SelectedJobState;
  @Input() jobSourcing: JobSourcing;
  @Input() boardsToCheckout: JobBoard[];
  @Output() loadingState = new EventEmitter<boolean>();
  routeSub$: Subscription;

  actions = [];

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _translate: TranslateService,
    private _snackbar: MatSnackBar,
    public dialog: MatDialog,
    private store: Store<fromRoot.State>,
    private jobStore: Store<fromJobs.State>
  ) {
    this.pendingRequest$ = this.store
      .select(fromRoot.getProgressBarStatus)
      .pipe(
        startWith(null),
        delay(0)
      );
    this.isJobValid$ = this.jobStore.select(fromJobs.isJobValid).pipe(
      startWith(null),
      delay(0)
    );
    this._translate
      .get('post_job_snackbar_form_invalid')
      .pipe(take(1))
      .subscribe(error_string => {
        this.fillRequiredMsg = error_string;
      });
    this._translate
      .get('settings_unit_alert_delete_job')
      .pipe(take(1))
      .subscribe(string => {
        this.deleteMsg = string;
      });
    this._translate
      .get('job_alert_delete_accept')
      .pipe(take(1))
      .subscribe(string => {
        this.delete = string;
      });

    this._translate
      .get('message_templates_snackbar_create_form_invalid')
      .pipe(take(1))
      .subscribe(string => {
        this.missingFieldsMsg = string;
      });

    switch (this._translate.currentLang) {
      case 'no': {
        moment.locale('nb');
        break;
      }
      default: {
        moment.locale('en');
      }
    }
  }

  @HostListener('window:beforeunload', ['$event']) unloadHandler(event: Event) {
    sessionStorage.setItem('is_reloaded', 'true');
  }

  ngOnInit() {
    const isReloaded = sessionStorage.getItem('is_reloaded') === 'true';
    if (isReloaded) {
      this._router.navigate(['info'], { relativeTo: this._route });
      sessionStorage.removeItem('is_reloaded');
    }

    this.watchRoutes();

    this.isJobValidSub$ = this.isJobValid$.subscribe(
      isValid => (this.isValid = isValid)
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.jobState) {
      const currentJob = changes.jobState.currentValue.job;
      if (!currentJob) {
        return;
      }
      if (currentJob.is_template) {
        this.actions = [
          {
            label: 'generic_delete',
            warn: true,
          },
          {
            label: 'generic_save',
          },
        ];
      } else if (currentJob.is_published) {
        this.actions = [
          { label: 'generic_save_draft' },
          { label: 'generic_unpublish' },
          { label: 'generic_duplicate' },
          { label: 'generic_delete', warn: true },
        ];
      } else {
        this.actions = [
          { label: 'generic_save_draft' },
          { label: 'generic_duplicate' },
          { label: 'generic_delete', warn: true },
        ];
      }
    } else {
      this.actions = [
        { label: 'generic_save' },
        { label: 'generic_duplicate' },
        { label: 'generic_delete', warn: true },
      ];
    }
  }

  ngOnDestroy() {
    if (this.routeSub$) {
      this.routeSub$.unsubscribe();
    }
    if (this.isJobValidSub$) {
      this.isJobValidSub$.unsubscribe();
    }
  }

  watchRoutes() {
    const getCurrentRoute = () => {
      const routeArr = this._router.url.split('/');
      this.currentRoute = routeArr[routeArr.length - 1];
    };

    getCurrentRoute();

    this.routeSub$ = this._router.events.subscribe(ev => {
      if (ev instanceof NavigationEnd) {
        getCurrentRoute();
      }
    });
  }

  cancelButtonPressed() {
    this._router.navigate(['dashboard/jobs/post'], {
      state: {
        isTemplate: this.jobState.job.is_template,
      },
    });
  }

  saveJobPressed() {
    const timeNow = new Date();
    this.store.dispatch(
      new jobsActions.SaveJob({
        job: this.jobState.job,
        listing: this.jobState.listing,
        showUpdateNotification: true,
      })
    );
    this.loadingState.emit(true);
  }

  checkoutAndPublishJobPressed() {
    if (this._isJobValid()) {
      if (this.isRoute('sourcing')) {
        this.dialog
          .open(CheckoutFormComponent, {
            data: {
              ordered_boards: this.boardsToCheckout,
            },
            minWidth: '40vw',
          })
          .afterClosed()
          .subscribe(result => {
            if (result && result.confirmed) {
              this.loadingState.emit(true);
              const timeNow = new Date();
              this.publishJobPressed();
              this.store.dispatch(
                new PurchaseJobSourcingBoard({
                  jobId: this.jobSourcing.job_id,
                })
              );
            }
          });
      } else {
        this.goToStep('sourcing');
      }
    } else {
      this._snackbar.open('Fill all required fields first', null, {
        panelClass: ['snackbar-50', 'snackbar-danger'],
        duration: 3000,
      });
    }
  }

  publishJobPressed() {
    if (this._isJobValid()) {
      this.loadingState.emit(true);
      const timeNow = new Date();
      this.store.dispatch(
        new jobsActions.SaveJob({
          job: this.jobState.job,
          listing: this.jobState.listing,
          showUpdateNotification: true,
          publish: true,
        })
      );
    } else {
      this._snackbar.open(this.missingFieldsMsg, null, {
        panelClass: ['snackbar-50', 'snackbar-danger'],
        duration: 3000,
      });
    }
  }

  deleteButtonPressed() {
    this._snackbar
      .open(this.deleteMsg, this.delete, {
        panelClass: ['snackbar-50', 'snackbar-danger'],
        duration: 8000,
      })
      .afterDismissed()
      .pipe(take(1))
      .subscribe(ev => {
        if (ev.dismissedByAction) {
          this.store.dispatch(
            new jobsActions.DeleteJob({
              jobId: this.jobState.job.id,
              listingId: this.jobState.listing.id,
            })
          );
          this.loadingState.emit(true);
        }
      });
  }

  isPublishable() {
    return this.jobState.job && !this.jobState.job.is_template;
  }

  getStep(page: number) {
    const isDone = page < pagesArr.indexOf(this.currentRoute);
    const isActive = this.currentRoute === pagesArr[page];
    const classes = `${isDone ? 'done' : isActive ? 'active' : ''}`;
    return classes;
  }

  goToStep(step: string) {
    this._router.navigate([step], { relativeTo: this._route });
  }

  goToNextStep() {
    const newRoute = pagesArr[pagesArr.indexOf(this.currentRoute) + 1];
    if (!newRoute) {
      return;
    }
    this.goToStep(newRoute);
  }

  goToStepIfPossible(step: number) {
    // TODO enable it after new store kicks in and we can validate all fields before posting job
    const newRoute = pagesArr[step];
    if (!newRoute) {
      return;
    }

    // if (this.isValid) {
    this.goToStep(newRoute);
    // } else {
    //     this._snackBar.open(this.fillRequiredMsg, null, {
    //         duration: 3000,
    //     });
    // }
  }

  goToPreviousStep() {
    const newRoute = pagesArr[pagesArr.indexOf(this.currentRoute) - 1];
    if (!newRoute) {
      return;
    }
    this.goToStep(newRoute);
  }

  showPreview() {
    const timeNow = new Date();
    this.store.dispatch(
      new jobsActions.ShowPreview({
        job: this.jobState.job,
        listing: this.jobState.listing,
        showUpdateNotification: true,
        lastUpdated: timeNow,
      })
    );
    this.loadingState.emit(true);
  }

  duplicatePressed() {
    this.store.dispatch(
      new jobsActions.CloneJob({
        jobId: this.jobState.job.id,
      })
    );
    this.loadingState.emit(true);
  }

  duplicateAsPressed() {
    this.store.dispatch(
      new jobsActions.CloneTemplateAsJob({
        jobId: this.jobState.job.id,
      })
    );
    this.loadingState.emit(true);
  }

  closePressed() {
    this.store.dispatch(
      new jobsActions.UnpublishJob({
        jobId: this.jobState.job.id,
      })
    );
    this.loadingState.emit(true);
  }

  dropdownHandler($event: string) {
    switch ($event) {
      case 'generic_save':
        return this.saveJobPressed();
      case 'generic_save_draft':
        return this.saveJobPressed();
      case 'generic_publish':
        return this.publishJobPressed();
      case 'generic_purchase_and_publish':
        return this.checkoutAndPublishJobPressed();
      case 'generic_duplicate':
        return this.duplicatePressed();
      case 'generic_unpublish':
        return this.closePressed();
      case 'generic_delete':
        return this.deleteButtonPressed();
    }
  }

  private isRoute(r: string) {
    // @ts-ignore
    // prettier-ignore
    // tslint:disable-next-line:max-line-length
    return this._route.snapshot._urlSegment.segments && this._route.snapshot._urlSegment.segments[this._route.snapshot._urlSegment.segments.length - 1].path === r;
  }

  private _isJobValid() {
    const jobTitle: string = this._deltaGetText(
      this.jobState.listing.job_listing_items.find(
        l => l.type === LISTING_ITEM_TYPE.LISTING_ITEM_HEADER
      ).title_rich
    );
    const aboutJobTitle: string = this._deltaGetText(
      this.jobState.listing.job_listing_items.find(
        l => l.type === LISTING_ITEM_TYPE.LISTING_ITEM_ABOUT_JOB
      ).title_rich
    );
    const aboutJobSubtitle: string = this._deltaGetText(
      this.jobState.listing.job_listing_items.find(
        l => l.type === LISTING_ITEM_TYPE.LISTING_ITEM_ABOUT_JOB
      ).subtitle_rich
    );
    return (
      this.jobState.job.title && jobTitle && aboutJobTitle && aboutJobSubtitle
    );
  }

  get timeAgo() {
    return this.jobState.lastUpdated
      ? moment(this.jobState.lastUpdated).fromNow()
      : '';
  }

  private _deltaGetText(inputDelta) {
    if (!inputDelta) {
      return '';
    }
    const delta = JSON.parse(inputDelta);
    // another check, better to be safe than sorry
    if (!(delta && delta['ops'] && delta['ops'].length)) {
      return '';
    }
    const tempCont = document.createElement('div');
    new Quill(tempCont).setContents(delta['ops']);
    return tempCont.getElementsByClassName('ql-editor')[0].textContent;
  }
}
